import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location, DatePipe } from '@angular/common';
import { saveAs } from 'file-saver';

import { environment } from '../../../environments/environment';
import { SalesReportService } from './sales-report.service';

import { SalesReport } from './models/sales-report';
import { LoaderService } from 'src/app/loader/loader.service';
import { calcularValorAdesao } from 'src/utils/adesao.utils';
import * as moment from 'moment';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { startWith, map, first, takeUntil } from 'rxjs/operators';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { UsersService } from 'src/app/users/users.service';
import { TeamsService } from 'src/app/teams/teams.service';
import { EquipeFind } from 'src/app/teams/models/equipe-find';
import { PermissionsService } from 'src/app/services/permissions.service';
import { User } from 'src/app/users/models/user';
import { PlanosService } from 'src/app/settings/planos/planos.service';
import { SalesReportFilters } from './models/sales-report-filters';

@Component({
  selector: 'app-sales-report',
  templateUrl: './sales-report.component.html',
  styleUrls: ['./sales-report.component.scss'],
})
export class SalesReportComponent implements OnInit {
  env;
  salesData: SalesReport[];
  salesData$: Observable<SalesReport[]>;

  page = 1;
  pageSize = 10;
  collectionSize = 0;
  filter = new FormControl('');

  sellersDropdownList = [];
  sellersDropdownSettings: IDropdownSettings = {};
  sellers: User[];

  plansDropdownList = [];
  plansDropdownSettings: IDropdownSettings = {};

  reportFilters: SalesReportFilters;

  private unsubscribe$ = new Subject();

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private location: Location,
    private salesService: SalesReportService,
    private loaderService: LoaderService,
    private usersService: UsersService,
    private teamsService: TeamsService,
    private plansService: PlanosService,
    public permissionsService: PermissionsService
  ) {
    this.env = environment;
  }

  async ngOnInit() {
    this.setInitialFilters();
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(async params => {
      await this.setDropdowns();
      this.getSales(this.env.selected.cnpj);
    });
  }

  async getSales(cnpj: string) {
    const loader = await this.loaderService.notifica({
      title: 'Gerando relatório...',
      date: moment().toISOString(),
      type: 'loading',
    });

    this.salesData = await this.salesService.getSales(cnpj, this.reportFilters).toPromise();

    this.salesData$ = this.filter.valueChanges.pipe(
      startWith(''),
      map(text => this.search(text))
    );

    loader.dismiss();
  }

  async refreshData() {
    this.getSales(this.env.selected.cnpj);
  }

  async exportToCSV() {
    const loader = await this.loaderService.notifica({
      title: 'Exportando relatório...',
      date: moment().toISOString(),
      type: 'loading',
    });

    this.salesService
      .exportToCSV(this.env.selected.cnpj, this.reportFilters)
      .pipe(first())
      .subscribe(res => {
        let fileBlob = res;

        let blob = new Blob([fileBlob], {
          type: 'text/csv',
        });

        saveAs(blob, 'sales-report-'.concat(String(+new Date())).concat('.csv'));

        loader.dismiss();
      });
  }

  setInitialFilters() {
    this.reportFilters = {
      teams: [],
      sellers: [],
      plans: [],
      salePeriod: { fromDate: null, toDate: null },
      syncAppPanelPeriod: { fromDate: null, toDate: null },
      approvalPeriod: { fromDate: null, toDate: null },
      syncErpPeriod: { fromDate: null, toDate: null },
    };
  }

  async setDropdowns() {
    await this.setSellersDropdown();
    await this.setPlansDropdown();
  }

  async setPlansDropdown() {
    const plans = await this.plansService.getPlanos(this.env.selected.cnpj).toPromise();

    this.plansDropdownList = plans;
    this.plansDropdownSettings = {
      singleSelection: false,
      idField: 'codigo',
      textField: 'descricao',
      selectAllText: 'Selecionar Todos',
      unSelectAllText: 'Limpar Seleção',
      itemsShowLimit: 1,
      allowSearchFilter: true,
      searchPlaceholderText: 'Pesquisar',
      noDataAvailablePlaceholderText: 'Sem dados disponíveis',
    };
  }

  async setSellersDropdown() {
    const sellers = await this.getSellers();

    this.sellersDropdownList = sellers;
    this.sellersDropdownSettings = {
      singleSelection: false,
      idField: 'codigo_externo',
      textField: 'nome',
      selectAllText: 'Selecionar Todos',
      unSelectAllText: 'Limpar Seleção',
      itemsShowLimit: 1,
      allowSearchFilter: true,
      searchPlaceholderText: 'Pesquisar',
      noDataAvailablePlaceholderText: 'Sem dados disponíveis',
    };
  }

  async getSellers() {
    const users = await this.usersService.getUsers(this.env.selected.cnpj).toPromise();
    this.sellers = users.filter(user => user.novoPerfil.nome.includes('Vendedor'));

    let supervisorTeams: EquipeFind[] = [];
    let supervisorSellers = [];
    let selectedSellers = [];

    if (this.permissionsService.isProfile('Supervisor')) {
      supervisorTeams = await this.teamsService.getUserTeams(this.env.user._id).toPromise();
      supervisorSellers = supervisorTeams.reduce((users, currentTeam) => {
        return [...users, ...currentTeam.usuarios];
      }, []);
      selectedSellers = this.sellers.reduce((sellers, currentUser) => {
        return supervisorSellers.find(seller => currentUser._id === seller._id) ? [...sellers, currentUser] : sellers;
      }, []);

      // Team filter to supervisor profile
      this.reportFilters.teams = supervisorTeams.map(team => team._id);
    } else {
      selectedSellers = this.sellers;
    }

    return selectedSellers;
  }

  search(text: string): SalesReport[] {
    const salesDataFiltered: SalesReport[] = this.salesData.filter(sale => {
      const term = text.toLowerCase();
      const datePipe = new DatePipe('pt');

      return (
        datePipe.transform(sale.data_da_criacao_app, 'dd/MM/yyyy HH:mm:ss').includes(term) ||
        sale.plano.descricao.toLowerCase().includes(term) ||
        sale.titular.nome.toLowerCase().includes(term) ||
        this.getValorAdesao(sale).toString().toLowerCase().includes(term) ||
        (sale.adiantamento.quantidade * sale.adiantamento.valor).toString().toLowerCase().includes(term) ||
        sale.valor_total.toString().toLowerCase().includes(term) ||
        sale.vendedor.nome.toLowerCase().includes(term)
      );
    });

    this.page = 1;
    this.collectionSize = salesDataFiltered.length;

    return salesDataFiltered;
  }

  identify(index, item) {
    if (!item) return null;
    return item._id;
  }

  goBack(): void {
    this.location.back();
  }

  getValorAdesao(sale: SalesReport): number {
    const adesao = sale && sale.adesao;
    return calcularValorAdesao(adesao && adesao.valor, adesao && adesao.desconto);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
